import Alert from "@mui/material/Alert";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  ResourceView,
  useResourceView,
  type ActiveFiltersState,
  type Filter,
  type FilterDefinition,
  type FilterOptionsResolver,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import { StatusBadge, STATUS_BADGES } from "@/shared/components/StatusBadge";
import { FormattedDateTime } from "@/shared/datetime";
import {
  ADMIN_FEEDBACK_LABEL_LIMIT,
  type AdminService,
} from "@/shared/domain/admin";
import {
  adminFeedbackLabelsQueryOptions,
  useAdminFeedback,
} from "@/shared/domain/admin/hooks/useAdminFeedback";
import {
  type AgentFeedbackRecord,
  FEEDBACK_CATEGORY_LABELS,
  FeedbackCategory,
  FeedbackSentiment,
} from "@/shared/domain/agentThread";
import { Copy, Download } from "@/shared/foundation/icons";
import { Tag, Tooltip } from "@/shared/foundation/primitives";
import {
  ColumnVisibility,
  DataTable,
  cellStyles,
  colId,
  createColumnHelper,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./adminTable.module.css";
import { EQUALS_ONLY } from "./adminThreadFilters";
import { filtersToFeedbackFilters } from "./feedbackFilters";
import { FeedbackSentimentIcon } from "./FeedbackSentimentIcon";
import {
  type FeedbackExportTarget,
  useFeedbackExport,
} from "./useFeedbackExport";

const columnHelper = createColumnHelper<AgentFeedbackRecord>();

const SENTIMENT_OPTIONS = [
  { label: "Positive", value: FeedbackSentiment.POSITIVE },
  { label: "Negative", value: FeedbackSentiment.NEGATIVE },
];

const CATEGORY_OPTIONS = Object.values(FeedbackCategory).map((c) => ({
  label: FEEDBACK_CATEGORY_LABELS[c],
  value: c,
}));

/**
 * Mutable bag the column cell renderers dereference at render time. Stashed in
 * a ref so the column array stays referentially stable (AG Grid treats a fresh
 * column array as a structural reset) — see the matching note on the threads
 * table's ``CellContext``.
 */
interface CellContext {
  selectedFeedbackId: string | undefined;
  onSelect: (feedback: AgentFeedbackRecord) => void;
}

function CategoriesCell({
  categories,
}: {
  categories: AgentFeedbackRecord["categories"];
}): React.ReactNode {
  return (
    <span className={styles.tagList}>
      {categories.map((c) => (
        <Tag key={c} label={FEEDBACK_CATEGORY_LABELS[c]} size="compact" />
      ))}
    </span>
  );
}

/**
 * A free-text note clipped to its cell, with the note (line breaks kept) in a
 * tooltip on hover or keyboard focus. The tooltip cuts off a very long note;
 * the selected row's detail card shows all of it.
 */
function NoteCell({
  note,
  testId,
}: {
  note: string | null | undefined;
  testId: string;
}): React.ReactNode {
  if (!note) {
    return <span className={styles.emptyNote}>-</span>;
  }
  return (
    <Tooltip>
      <Tooltip.Trigger
        delay={150}
        render={
          <span className={styles.noteCell} tabIndex={0} data-testid={testId}>
            {note}
          </span>
        }
      />
      <Tooltip.Content className={styles.noteTooltip}>{note}</Tooltip.Content>
    </Tooltip>
  );
}

function AdminLabelCell({
  row,
}: {
  row: AgentFeedbackRecord;
}): React.ReactNode {
  // ``admin_label`` when non-empty, else the resolved/open state word. ``||``
  // (not ``??``) so a cleared-to-empty label reverts to the state word.
  // Tone carries the resolved signal so the text never has to say it twice.
  const status = row.resolved
    ? STATUS_BADGES.agentFeedback.resolved
    : STATUS_BADGES.agentFeedback.open;
  return (
    <StatusBadge
      status={status}
      label={row.admin_label || undefined}
      data-testid="feedback-admin-label"
    />
  );
}

/**
 * Admin feedback columns. Reads live selection/handlers off ``ctxRef`` at
 * render time so the array itself never changes identity. ``When`` doubles as
 * the row's open affordance — DataTable exposes no row-click, so a cell link
 * carries the interaction, the same way the threads table selects via its
 * Thread ID / Title cells.
 */
function buildColumns(
  ctxRef: React.MutableRefObject<CellContext>,
): ColDef<AgentFeedbackRecord>[] {
  return [
    columnHelper.accessor("created", {
      headerName: "When",
      width: 190,
      cellRenderer: (p: ICellRendererParams<AgentFeedbackRecord, string>) => {
        const row = p.data;
        if (!row) {
          return null;
        }
        const selected = ctxRef.current.selectedFeedbackId === row.feedback_id;
        return (
          <button
            type="button"
            className={`${styles.linkButton} ${selected ? styles.linkButtonActive : ""}`}
            onClick={() => ctxRef.current.onSelect(row)}
          >
            <FormattedDateTime value={row.created} fallback="-" />
          </button>
        );
      },
    }),
    // Not row fields — sentiment/categories/label render bespoke cells, so
    // these carry no accessor (and stay unsortable).
    {
      colId: "sentiment",
      headerName: "Sentiment",
      width: 100,
      sortable: false,
      cellClass: cellStyles.tagsCell,
      cellRenderer: (p: ICellRendererParams<AgentFeedbackRecord>) =>
        p.data ? <FeedbackSentimentIcon sentiment={p.data.sentiment} /> : null,
    },
    {
      colId: "categories",
      headerName: "Categories",
      flex: 1,
      minWidth: 220,
      sortable: false,
      cellClass: cellStyles.tagsCell,
      cellRenderer: (p: ICellRendererParams<AgentFeedbackRecord>) =>
        p.data ? <CategoriesCell categories={p.data.categories} /> : null,
    },
    columnHelper.accessor("notes", {
      headerName: "Notes",
      flex: 2,
      minWidth: 220,
      cellRenderer: (
        p: ICellRendererParams<AgentFeedbackRecord, string | null | undefined>,
      ) => <NoteCell note={p.value} testId="feedback-note" />,
    }),
    columnHelper.accessor("created_by", { headerName: "User", width: 190 }),
    columnHelper.accessor("org_id", { headerName: "Org", width: 160 }),
    columnHelper.accessor("thread_id", {
      headerName: "Session",
      width: 170,
      cellClass: cellStyles.monoCell,
    }),
    columnHelper.accessor("message_sequence_num", {
      headerName: "Msg #",
      width: 100,
    }),
    {
      colId: "admin_label",
      headerName: "Admin label",
      width: 140,
      sortable: false,
      cellClass: cellStyles.tagsCell,
      cellRenderer: (p: ICellRendererParams<AgentFeedbackRecord>) =>
        p.data ? <AdminLabelCell row={p.data} /> : null,
    },
    columnHelper.accessor("admin_note", {
      headerName: "Admin note",
      flex: 1,
      minWidth: 180,
      cellRenderer: (
        p: ICellRendererParams<AgentFeedbackRecord, string | null | undefined>,
      ) => <NoteCell note={p.value} testId="feedback-admin-note" />,
    }),
  ];
}

/** Hidden until picked in the column menu: most rows have no admin note yet. */
const HIDDEN_BY_DEFAULT = new Set(["admin_note"]);

/**
 * Options for the Admin label filter: the labels in use containing the search
 * text, most-used first. The server caps the list, so a full page is reported
 * as truncated and the picker keeps searching the server as the admin types.
 * The empty search shares the triage form's label query, which a triage save
 * invalidates, so a label just saved is listed the next time the filter opens.
 */
function buildAdminLabelOptionResolver(
  queryClient: QueryClient,
  adminService: AdminService,
): FilterOptionsResolver {
  return async (search) => {
    const labels = await queryClient.fetchQuery(
      adminFeedbackLabelsQueryOptions(adminService, search),
    );
    return {
      options: labels.map(({ label }) => ({ label, value: label })),
      truncated: labels.length >= ADMIN_FEEDBACK_LABEL_LIMIT,
    };
  };
}

interface FeedbackTableProps {
  /** Lazy server-search resolver for the Org filter dropdown. */
  orgOptions: FilterOptionsResolver;
  /** Lazy server-search resolver for the User filter dropdown. */
  userOptions: FilterOptionsResolver;
  /**
   * Seeds the Session (``thread_id``) filter at mount — used by deep links
   * (``?sessionId=``/``?feedbackId=``) and the threads table's "show feedback
   * for this thread" hand-off. Captured once; the table owns filter state
   * after that. The parent remounts the view (per view-mode switch) when a new
   * seed needs to apply.
   */
  initialSessionId?: string | undefined;
  onSelectFeedback: (feedback: AgentFeedbackRecord) => void;
  selectedFeedbackId: string | undefined;
}

/**
 * Admin feedback table on the ResourceView/DataTable stack — the structural
 * twin of {@link AgentThreadsTable}. Owns its own filters, pagination, and
 * fetch; selecting a row's ``When`` cell lifts the feedback record to the
 * parent, which renders the triage panel below.
 */
export function FeedbackTable({
  orgOptions,
  userOptions,
  initialSessionId,
  onSelectFeedback,
  selectedFeedbackId,
}: FeedbackTableProps) {
  // Live cell context, refreshed every render so the column array can stay
  // referentially stable (see CellContext).
  const ctxRef = React.useRef<CellContext>({
    selectedFeedbackId,
    onSelect: onSelectFeedback,
  });

  const columns = React.useMemo(() => buildColumns(ctxRef), []);
  const queryClient = useQueryClient();
  const { adminService } = useDomainServices();
  const adminLabelOptions = React.useMemo(
    () => buildAdminLabelOptionResolver(queryClient, adminService),
    [queryClient, adminService],
  );

  const columnOptions = React.useMemo<ColumnPickerOption[]>(
    () =>
      columns.map((c) => ({ id: colId(c), label: c.headerName ?? colId(c) })),
    [columns],
  );

  const filterDefinitions = React.useMemo<FilterDefinition[]>(
    () => [
      {
        type: "enum",
        field: "sentiment",
        name: "Sentiment",
        options: SENTIMENT_OPTIONS,
      },
      {
        type: "enum",
        field: "category",
        name: "Category",
        options: CATEGORY_OPTIONS,
      },
      {
        type: "enum",
        field: "org_id",
        name: "Org",
        options: orgOptions,
      },
      {
        type: "enum",
        field: "created_by",
        name: "User",
        options: userOptions,
      },
      {
        type: "string",
        field: "thread_id",
        name: "Session",
        allowedComparators: EQUALS_ONLY,
      },
      {
        type: "enum",
        field: "admin_label",
        name: "Admin label",
        options: adminLabelOptions,
      },
      { type: "boolean", field: "resolved", name: "Resolved" },
      { type: "boolean", field: "has_notes", name: "Has notes" },
      { type: "boolean", field: "has_errors", name: "Has errors" },
      { type: "boolean", field: "is_customer", name: "Customer only" },
    ],
    [orgOptions, userOptions, adminLabelOptions],
  );

  // Seed the Session filter from a deep link / cross-view hand-off. Captured
  // once by useResourceView; the view remounts (per view-mode switch) when a
  // different seed needs to take effect.
  const initialActiveFilters = React.useMemo<ActiveFiltersState | undefined>(
    () =>
      initialSessionId
        ? {
            type: "structured",
            matchMode: "AND",
            filters: [
              {
                type: "string",
                field: "thread_id",
                comparator: "EQUALS",
                values: [initialSessionId],
              },
            ],
          }
        : undefined,
    [initialSessionId],
  );

  const view = useResourceView<AgentFeedbackRecord>({
    getRowId: (row) => row.feedback_id,
    defaultVisibleColumnIds: columns
      .map(colId)
      .filter((id) => !HIDDEN_BY_DEFAULT.has(id)),
    initialActiveFilters,
  });

  const feedbackFilters = React.useMemo(() => {
    const userFilters: Filter[] =
      view.filters.type === "structured" ? view.filters.filters : [];
    return filtersToFeedbackFilters(userFilters);
  }, [view.filters]);

  // Forward pagination via a `page → after-token` map (the same token-chain
  // idiom as the threads table). Reset when the filters or page size change —
  // the cursors are only valid for the search that produced them (useResourceView
  // already resets currentPage on those transitions).
  const [tokenChain, setTokenChain] = React.useState<Map<number, string>>(
    new Map(),
  );
  React.useEffect(() => {
    setTokenChain(new Map());
  }, [view.filters, view.table.pageSize]);

  const after =
    view.table.currentPage > 0
      ? tokenChain.get(view.table.currentPage)
      : undefined;

  const query = useAdminFeedback({
    ...feedbackFilters,
    pageToken: after,
    limit: view.table.pageSize,
  });

  const nextToken = query.data?.next_token ?? null;
  const currentPage = view.table.currentPage;
  React.useEffect(() => {
    if (nextToken === null) {
      return;
    }
    setTokenChain((prev) => {
      if (prev.get(currentPage + 1) === nextToken) {
        return prev;
      }
      const next = new Map(prev);
      next.set(currentPage + 1, nextToken);
      return next;
    });
  }, [nextToken, currentPage]);

  // Keep the cell-context ref current, then repaint the cells that read it.
  ctxRef.current = { selectedFeedbackId, onSelect: onSelectFeedback };
  const refreshCells = view.table.refreshCells;
  React.useEffect(() => {
    refreshCells();
  }, [refreshCells, selectedFeedbackId]);

  const exportFeedback = useFeedbackExport();
  const showNotification = useNotification();
  const selectionOptions = React.useMemo<
    SelectionOption<AgentFeedbackRecord>[]
  >(() => {
    const exportTo =
      (target: FeedbackExportTarget) =>
      async ({ selected }: { selected: AgentFeedbackRecord[] }) => {
        // The Selection menu doesn't catch a rejected action, so an export
        // failure (a denied clipboard write, say) surfaces here as a toast.
        try {
          await exportFeedback(selected, target);
          const noun = selected.length === 1 ? "entry" : "entries";
          showNotification(
            target === "clipboard"
              ? `Copied ${selected.length} feedback ${noun} for AI`
              : `Downloaded ${selected.length} feedback ${noun}`,
            "success",
            2500,
          );
        } catch (error) {
          ErrorMonitoringService.captureError(error, {
            action: "export admin feedback",
            target,
            count: selected.length,
          });
          showNotification(
            `Export failed: ${error instanceof Error ? error.message : "unknown error"}`,
            "error",
            6000,
          );
        }
      };
    return [
      {
        name: "Download for AI (.md)",
        icon: <Download />,
        action: exportTo("download"),
      },
      {
        name: "Copy for AI",
        icon: <Copy />,
        action: exportTo("clipboard"),
      },
    ];
  }, [exportFeedback, showNotification]);

  const loading = deriveLoadingState({
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  });

  const rows = query.data?.items ?? [];

  if (query.isError) {
    return (
      <Alert severity="error" className={styles.errorBanner}>
        Failed to load feedback. {query.error?.message ?? ""}
      </Alert>
    );
  }

  return (
    <div className={styles.tableSlot}>
      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={columnOptions}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          <ResourceView.Selection<AgentFeedbackRecord>
            selection={view.table.selection}
            clearSelection={view.table.clearSelection}
            options={selectionOptions}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<AgentFeedbackRecord>
          table={view.table}
          hasNextPage={Boolean(query.data?.next_token)}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No feedback found."
          selectionEnabled
          ariaLabel="Feedback"
        />
      </ResourceView.Root>
    </div>
  );
}
